import load from '#core/load';


const SLOT_MAIN = '[data-story-video-slot="main"]';
const SLOT_SECONDARY = '[data-story-video-slot="secondary"]';
const LABEL = '[data-story-video-label]';
const VIDEO_FIGURE = '.video';
const MEDIA_CONTAINER = '.media-figure__media';
const OVERLINK = '.youtube-overlink, .vimeo-overlink';
const CONTROL = '.video__control';
const HTML_VIDEO = '.video__source';


const module = {
	init() {
		if (load.ran('story-video')) return false;
		load.content(() => {
			document.querySelectorAll('[data-story-video]').forEach(init_instance);
		});
	},
};

export default module;
module.init();


/**
 * Initialize a story-video instance
 * @param {HTMLElement} $root
 */
function init_instance($root) {
	const $main_slot = $root.querySelector(SLOT_MAIN);
	const $secondary_slot = $root.querySelector(SLOT_SECONDARY);
	const $label = $root.querySelector(LABEL);

	if (!$secondary_slot) return;

	const excerpt_label = $root.dataset.excerptLabel;
	const full_label = $root.dataset.fullLabel;

	let excerpt_is_main = true;

	// Intercept overlink clicks in capture phase (before video.js handlers)
	$root.addEventListener('click', (e) => {
		const $overlink = e.target.closest(OVERLINK);
		if (!$overlink) return;

		e.stopPropagation();
		e.preventDefault();

		const $slot = $overlink.closest('[data-story-video-slot]');
		if (!$slot) return;

		if ($slot === $main_slot) {
			play_in_slot($main_slot);
		} else {
			swap_and_play();
		}
	}, true);

	// Handle clicks on the secondary slot for videos without overlinks
	// (bare Vimeo iframes, HTML video elements). The dark ::after overlay
	// on .media-figure__media catches these clicks above the iframe.
	$secondary_slot.addEventListener('click', (e) => {
		// Overlinks are handled by the capture-phase listener above.
		// The playback-toggle inside .video__control synthesizes its own
		// overlink click which triggers the swap via that capture listener —
		// handling it here too would swap twice and cancel out.
		if (e.target.closest(OVERLINK)) return;
		if (e.target.closest(CONTROL)) return;

		swap_and_play();
	});


	/**
	 * Swap the two videos and autoplay the new main
	 */
	function swap_and_play() {
		const $main_video = $main_slot.querySelector(VIDEO_FIGURE);
		const $secondary_video = $secondary_slot.querySelector(VIDEO_FIGURE);
		if (!$main_video || !$secondary_video) return;

		// Pause/reset the current main before moving it to secondary
		pause_video($main_video);

		// Swap figure elements between slots
		$main_slot.appendChild($secondary_video);
		$secondary_slot.appendChild($main_video);

		// Update label
		excerpt_is_main = !excerpt_is_main;
		$label.textContent = excerpt_is_main ? full_label : excerpt_label;

		// Autoplay the new main
		play_in_slot($main_slot);
	}
}


/**
 * Start playback in the given slot's video
 * @param {HTMLElement} $slot
 */
function play_in_slot($slot) {
	const $figure = $slot.querySelector(VIDEO_FIGURE);
	if (!$figure) return;

	const $container = $slot.querySelector(MEDIA_CONTAINER);
	if (!$container) return;

	// HTML <video> element
	const $html_video = $container.querySelector(HTML_VIDEO);
	if ($html_video) {
		$html_video.play();
		$figure.setAttribute('data-playing', 'true');
		return;
	}

	// Existing bare iframe (e.g. Vimeo without thumbnail image)
	const $existing_iframe = $container.querySelector('iframe');
	if ($existing_iframe) {
		// Resume playback via Vimeo postMessage API
		$existing_iframe.contentWindow?.postMessage(
			JSON.stringify({ method: 'play' }), '*'
		);
		$figure.setAttribute('data-playing', 'true');
		return;
	}

	// Create iframe from overlink (YouTube or Vimeo with thumbnail)
	const $overlink = $container.querySelector(OVERLINK);
	if (!$overlink) return;

	const url = $overlink.getAttribute('href');
	const $iframe = document.createElement('iframe');
	$iframe.src = url;
	$iframe.setAttribute('frameborder', '0');
	$iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
	$iframe.setAttribute('allowfullscreen', 'true');

	$container.prepend($iframe);

	// Hide overlink and control (keep in DOM for reset)
	$overlink.hidden = true;
	const $control = $slot.querySelector(CONTROL);
	if ($control) $control.hidden = true;

	$figure.setAttribute('data-playing', 'true');
}


/**
 * Pause/reset a video figure when moving it out of main
 * @param {HTMLElement} $figure - The .video figure element
 */
function pause_video($figure) {
	// HTML <video> element
	const $html_video = $figure.querySelector(HTML_VIDEO);
	if ($html_video) {
		$html_video.pause();
		$figure.setAttribute('data-playing', 'false');
		return;
	}

	// Iframe created from overlink click — remove it, restore thumbnail
	const $overlink = $figure.querySelector(OVERLINK);
	if ($overlink && $overlink.hidden) {
		const $iframe = $figure.querySelector('iframe');
		if ($iframe) $iframe.remove();

		$overlink.hidden = false;

		const $control = $figure.querySelector(CONTROL);
		if ($control) $control.hidden = false;

		$figure.setAttribute('data-playing', 'false');
		return;
	}

	// Bare iframe (e.g. Vimeo without thumbnail) — pause via postMessage
	const $iframe = $figure.querySelector('iframe');
	if ($iframe) {
		$iframe.contentWindow?.postMessage(
			JSON.stringify({ method: 'pause' }), '*'
		);
		$figure.setAttribute('data-playing', 'false');
	}
}
